import { setContext } from "@sentry/react"
import bowser from "bowser"
import { pdfjs } from "react-pdf"
import { IS_APP } from "../vuplex/constants"

// Even when using legacy worker for unsupported browsers
// those two tend to cause issues. I think react-pdf defaults
// to modern build of pdfjs no matter which worker is used
// and that's the reason. We'll include polyfills to be safe.
import "core-js/proposals/promise-with-resolvers"
import "core-js/proposals/url"

export const initializePDFJsWorker = (): void => {
  const parser = bowser.getParser(window.navigator.userAgent)
  const browser = parser.getBrowser()

  const isBrowserSupported = getIsBrowserSupported({
    name: browser.name ?? "",
    version: browser.version ?? "",
  })

  const shouldUseLegacyPDFWorker = !isBrowserSupported || IS_APP

  if (shouldUseLegacyPDFWorker) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
      import.meta.url
    ).toString()
  } else {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url
    ).toString()
  }

  setContext("PDF Viewer", {
    "Should use legacy worker?": shouldUseLegacyPDFWorker,
    "Does use legacy worker?":
      pdfjs.GlobalWorkerOptions.workerSrc.includes("legacy"),
  })
}

/**
 * Checks user's browser compatibility with the PDF worker.
 * Currently PDF.js is pinned to version 5.3.31. Browsers not supporting
 * all features of this version will use legacy PDF.js worker instead.
 *
 * Browser versions were chosen by their support for URL.parse()
 * Versions BELOW the specified one are considered legacy
 */
export const getIsBrowserSupported = ({
  name,
  version,
}: {
  name: string
  version: string
}) => {
  const supportedBrowsers = [
    { name: "Chrome", vMajor: 126, vMinor: 0 },
    { name: "Microsoft Edge", vMajor: 126, vMinor: 0 },
    { name: "Firefox", vMajor: 126, vMinor: 0 },
    { name: "Safari", vMajor: 18, vMinor: 0 },
  ]

  const browserVersion = version.split(".") ?? []
  const versionMajor = Number(browserVersion[0]) || 0
  const versionMinor = Number(browserVersion[1]) || 0

  const isBrowserSupported = supportedBrowsers.some((browser) => {
    const isMajorVersionSupported = versionMajor >= browser.vMajor
    const isMinorVersionSupported =
      versionMajor === browser.vMajor ? versionMinor >= browser.vMinor : true

    const isBrowserNameSupported = browser.name === name
    const isBrowserVersionSupported =
      isMajorVersionSupported && isMinorVersionSupported

    return isBrowserNameSupported && isBrowserVersionSupported
  })

  return isBrowserSupported
}
